import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { appendLog } from "./logger";

const CHROME_BG = "#F5F6F8";

/**
 * Status bar + safe-area: keep system clock readable and header clear of insets.
 * Prefer non-overlay on Android so WebView sits below the status bar (env() often 0).
 */
export async function configureNativeChrome(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (Capacitor.isPluginAvailable("StatusBar")) {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: CHROME_BG });
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.show();
    }
  } catch (err) {
    appendLog(
      "warn",
      "chrome",
      "StatusBar configure failed",
      err instanceof Error ? err.message : err
    );
  }

  // Capacitor / Android sometimes leave safe-area env at 0; pad chrome if needed.
  try {
    const top = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--safe-top"
      ) || "0"
    );
    if (!Number.isFinite(top) || top < 1) {
      // Approximate status-bar height when overlay is off this is unused;
      // when insets are missing under overlay, use a modest fallback.
      document.documentElement.style.setProperty("--safe-top-fallback", "0px");
    }
  } catch {
    /* ignore */
  }
}

/** Unregister any leftover PWA service worker (stale chunks break first PDF open after OTA). */
export async function clearStaleServiceWorkers(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    if (regs.length) {
      appendLog("info", "boot", `unregistered ${regs.length} service worker(s)`);
    }
  } catch (err) {
    appendLog(
      "warn",
      "boot",
      "SW unregister failed",
      err instanceof Error ? err.message : err
    );
  }
}
